/**
 * Development Tools for Testing Plan Restrictions
 *
 * IMPORTANT: Only works in development mode (import.meta.env.DEV)
 *
 * Usage in browser console:
 * ```
 * // View current plan override
 * window.devTools.getPlanOverride()
 *
 * // Set plan override
 * window.devTools.setPlanOverride('FREE')
 * window.devTools.setPlanOverride('PRO')
 * window.devTools.setPlanOverride('ENTERPRISE')
 *
 * // Clear plan override
 * window.devTools.clearPlanOverride()
 * ```
 */

import type { Plan } from '@deepguide-ai/types';

const DEV_PLAN_OVERRIDE_KEY = '__dev_plan_override__';

export interface DevTools {
  setPlanOverride: (plan: Plan) => void;
  getPlanOverride: () => Plan | null;
  clearPlanOverride: () => void;
  listPlans: () => Plan[];
}

/**
 * Get the current plan override (dev mode only)
 */
export function getDevPlanOverride(): Plan | null {
  if (!import.meta.env.DEV) return null;

  const override = localStorage.getItem(DEV_PLAN_OVERRIDE_KEY);
  return override as Plan | null;
}

/**
 * Set a plan override for testing (dev mode only)
 */
export function setDevPlanOverride(plan: Plan): void {
  if (!import.meta.env.DEV) {
    console.warn('Plan override only works in development mode');
    return;
  }

  localStorage.setItem(DEV_PLAN_OVERRIDE_KEY, plan);
  console.log(`✅ Plan override set to: ${plan}`);
  console.log('🔄 Reload the page for changes to take effect');
}

/**
 * Clear the plan override (dev mode only)
 */
export function clearDevPlanOverride(): void {
  if (!import.meta.env.DEV) {
    console.warn('Plan override only works in development mode');
    return;
  }

  localStorage.removeItem(DEV_PLAN_OVERRIDE_KEY);
  console.log('✅ Plan override cleared');
  console.log('🔄 Reload the page for changes to take effect');
}

/**
 * List all available plans
 */
export function listAvailablePlans(): Plan[] {
  return ['FREE', 'PRO', 'ENTERPRISE'];
}

/**
 * Initialize dev tools on window object (dev mode only)
 */
export function initDevTools(): void {
  if (!import.meta.env.DEV) return;

  const devTools: DevTools = {
    setPlanOverride: setDevPlanOverride,
    getPlanOverride: getDevPlanOverride,
    clearPlanOverride: clearDevPlanOverride,
    listPlans: listAvailablePlans,
  };

  // Attach to window for console access
  (window as Window & { devTools: DevTools }).devTools = devTools;

  // Log helpful message
  const currentOverride = getDevPlanOverride();
  if (currentOverride) {
    console.log(`🔧 Dev Tools: Plan override active (${currentOverride})`);
    console.log('   Use window.devTools.clearPlanOverride() to remove');
  } else {
    console.log('🔧 Dev Tools: Available commands:');
    console.log('   window.devTools.setPlanOverride("FREE|PRO|ENTERPRISE")');
    console.log('   window.devTools.getPlanOverride()');
    console.log('   window.devTools.clearPlanOverride()');
    console.log('   window.devTools.listPlans()');
  }
}
